import { defineStore } from 'pinia'
import { ref } from 'vue'
import { BASE_URL } from '../api/environment'
import { DEFAULT_THEME, aplicarTemaDocumento, normalizarNomeTema } from '@/utils/theme'

export const useEmpresaStore = defineStore(
    'empresa',
    () => {
        const empresaAtual = ref(null)
        const listaEmpresas = ref([])
        const isAdmin = ref(false)

        function applyActiveTheme() {
            return aplicarTemaDocumento(empresaAtual.value?.company_color || DEFAULT_THEME)
        }

        function encontrarEmpresa(empresa) {
            if (!empresa) return null

            const userCompanyUuid =
                typeof empresa === 'object' ? empresa.user_company_uuid : empresa

            const companyId = typeof empresa === 'object' ? empresa.company_id || empresa.id : null

            return (
                listaEmpresas.value.find((item) => item.user_company_uuid === userCompanyUuid) ||
                listaEmpresas.value.find(
                    (item) => companyId !== null && String(item.company_id) === String(companyId),
                ) ||
                null
            )
        }

        function definirEmpresaAtual(empresa) {
            if (!empresa) {
                empresaAtual.value = null
                isAdmin.value = false
                applyActiveTheme()
                return true
            }

            const empresaEncontrada = encontrarEmpresa(empresa)

            if (!empresaEncontrada) return false

            empresaAtual.value = {
                id: empresaEncontrada.company_id,
                user_company_uuid: empresaEncontrada.user_company_uuid,
                company_name: empresaEncontrada.company_name,
                company_logo_path: empresaEncontrada.company_logo_path,
                company_role: normalizarCompanyRole(empresaEncontrada.company_role),
                company_color: normalizarNomeTema(empresaEncontrada.company_color),
                system_role: empresaEncontrada.system_role,
                access: empresaEncontrada.access,
                tenant_id: empresaEncontrada.tenant_id,
            }

            isAdmin.value = empresaEncontrada.system_role === 'super'

            applyActiveTheme()

            return true
        }

        function getUserCompanyId() {
            return empresaAtual.value?.user_company_uuid
        }

        function getCompanyRole() {
            return empresaAtual.value?.company_role
        }

        function getAcess() {
            return empresaAtual.value?.access
        }

        function atualizarEmpresaAtual(data) {
            const empresaIndex = getIndexCompany()
            const companyColor = normalizarNomeTema(
                data.color_palette || data.company_color || empresaAtual.value?.company_color,
            )

            empresaAtual.value = {
                id: data.id || getId(),

                user_company_uuid: getUserCompanyId(),

                company_name: data.name || data.company_name || empresaAtual.value?.company_name,

                company_logo_path: getLogo(),

                company_role: normalizarCompanyRole(getCompanyRole()),

                company_color: companyColor,

                system_role: getSystemRole(),

                access: getAcess(),

                tenant_id: getTenantId(),
            }

            if (empresaIndex !== -1) {
                listaEmpresas.value[empresaIndex] = {
                    ...listaEmpresas.value[empresaIndex],
                    company_name: empresaAtual.value.company_name,
                    company_color: companyColor,
                }
            }

            applyActiveTheme()
        }

        function getSystemColor() {
            return empresaAtual.value?.company_color || 'defaultTheme'
        }

        function getId() {
            return empresaAtual.value?.id
        }

        function getTenantId() {
            return empresaAtual.value?.tenant_id
        }

        function getSystemRole() {
            return empresaAtual.value?.system_role
        }

        function setSystemTheme(color) {
            const empresaIndex = getIndexCompany()
            const theme = normalizarNomeTema(color)

            if (empresaIndex !== -1) {
                listaEmpresas.value[empresaIndex].company_color = theme
            }

            if (empresaAtual.value) {
                empresaAtual.value.company_color = theme
            }

            applyActiveTheme()
        }

        function setLogo(urlCompletaDaLogo) {
            const empresaIndex = getIndexCompany()

            if (empresaIndex !== -1) {
                const caminhoRelativo = urlCompletaDaLogo.replace(BASE_URL, '')

                listaEmpresas.value[empresaIndex].company_logo_path = caminhoRelativo

                if (empresaAtual.value) {
                    empresaAtual.value.company_logo_path = caminhoRelativo
                }
            }
        }

        function getLogo() {
            if (!empresaAtual.value) {
                return null
            }

            const empresaIndex = getIndexCompany()

            if (empresaIndex === -1) {
                return null
            }

            const empresa = listaEmpresas.value[empresaIndex]

            return empresa ? empresa.company_logo_path : null
        }

        function getIndexCompany() {
            if (!empresaAtual.value) {
                return -1
            }

            const userCompanyId = getUserCompanyId()

            if (!userCompanyId) {
                return -1
            }

            return listaEmpresas.value.findIndex(
                (empresa) => empresa.user_company_uuid == userCompanyId,
            )
        }

        function removeLogo() {
            const empresaIndex = getIndexCompany()

            if (empresaIndex !== -1) {
                listaEmpresas.value[empresaIndex].company_logo_path = null

                if (empresaAtual.value) {
                    empresaAtual.value.company_logo_path = null
                }
            }
        }

        function definirListaEmpresas(empresas) {
            if (Array.isArray(empresas)) {
                listaEmpresas.value = empresas.map((empresa) => ({
                    user_company_uuid: empresa.user_company_uuid,

                    company_id: empresa.company_id,

                    company_name: empresa.company_name,

                    company_color: normalizarNomeTema(empresa.company_color),

                    company_logo_path: empresa.company_logo_path,

                    company_role: normalizarCompanyRole(empresa.company_role),

                    system_role: empresa.system_role,

                    access: empresa.access,

                    tenant_id: empresa.tenant_id,
                }))
            } else {
                listaEmpresas.value = []
            }
        }

        function normalizarCompanyRole(role) {
            const value = String(
                role || '',
            ).toLowerCase()

            const map = {
                admin_company: 'company_admin',
                user_company: 'company_user',
                company_admin: 'company_admin',
                company_user: 'company_user',
                sys_admin: 'sys_admin',
                sys_user: 'sys_user',
                financial_manager:
                    'financial_manager',
            }

            return map[value] || value
        }

        function limparDadosEmpresa() {
            empresaAtual.value = null
            listaEmpresas.value = []
            isAdmin.value = false

            applyActiveTheme()
        }

        return {
            empresaAtual,
            listaEmpresas,
            isAdmin,
            getUserCompanyId,
            getIndexCompany,
            getCompanyRole,
            getSystemColor,
            getAcess,
            definirEmpresaAtual,
            definirListaEmpresas,
            atualizarEmpresaAtual,
            limparDadosEmpresa,
            getSystemRole,
            getId,
            getTenantId,
            setSystemTheme,
            setLogo,
            getLogo,
            removeLogo,
            applyActiveTheme,
        }
    },
    {
        persist: {
            key: 'empresa-store',
            paths: [
                'empresaAtual',
                'listaEmpresas',
            ],
        },
    },
)
